import { EMPTY_FIELD } from 'constants/emptyField';
import { Resource } from 'constants/resources';

import React, { ReactNode, useCallback } from 'react';

import { useCreatePathWithQuery } from 'hooks/useCreatePathWithQuery';
import get from 'lodash/get';
import { FunctionField } from 'react-admin';
import { Box, BoxProps } from 'shared/mui/Box';
import { FunctionFieldProps } from 'shared/react-admin/Fields';
import { Link } from 'shared/react-admin/Links/Link';

export interface EmbeddedFunctionFieldProps<TRecord extends Record<string, unknown>>
  extends Omit<FunctionFieldProps, 'render' | 'sx'>,
    Pick<BoxProps, 'sx'> {
  sourceField: DotNestedKeys<TRecord>;
  linkConfig?: {
    resource: Resource;
    type?: 'show' | 'edit';
    idField?: DotNestedKeys<TRecord>;
  };
  emptyText?: string;
  render?: (value: any) => ReactNode;
}

export const EmbeddedFunctionField = <TRecord extends Record<string, unknown>>({
  label,
  sourceField,
  linkConfig,
  emptyText = EMPTY_FIELD,
  render,
  sx,
  ...restProps
}: EmbeddedFunctionFieldProps<TRecord>) => {
  const { createPathWithQuery } = useCreatePathWithQuery();

  const renderField = useCallback(
    (record: TRecord) => {
      const value = get(record, sourceField);

      if (!value) {
        return <span>{emptyText}</span>;
      }

      if (linkConfig) {
        const { idField = '@id', resource, type = 'show' } = linkConfig;
        const id = get(record, idField);
        const path = createPathWithQuery({ resource, id, type });

        return <Link to={path}>{value}</Link>;
      }

      if (render) {
        return render(value);
      }

      return (
        <Box component="span" sx={sx}>
          {value}
        </Box>
      );
    },
    [sourceField, linkConfig, render, sx, emptyText, createPathWithQuery],
  );

  return <FunctionField {...restProps} label={label} render={renderField} />;
};
